/* eslint-disable */
import { intersection, isString, without, sortBy, throttle, get } from 'lodash'
import { updateContract, updateMarket, updateMarketContracts } from '@/utils/notifications';
import api from '@/services/api';
import ls from '@/helpers/localStorage';

import {
  ORDERS,
  TIMESPANS,
  AVAILABLE_TIMESPANS,
  ORDERS_WITH_TIMESPAN_FILTER,
  selectOrder,
  selectTimespan } from './filters_consts';

const DEFAULT_FILTERED_MARKETS = {
  data: [],
  page: 0,
  totalCount: 0,
  canLoadMore: true,
};

const TWENTY_FOUR_GAINS = 'Biggest 24 hour Gains';
const TWENTY_FOUR_LOSS = 'Biggest 24 hour Losses';
const WEEK_GAINS = 'Biggest 7 day Gains';
const WEEK_LOSS = 'Biggest 7 day Losses';

const prepareBiggestGains = (items, sectionTitle, isGain = false) => {
  items.forEach((el) => {
    el.sectionTitle = sectionTitle;
    el.isGain = isGain;
  });
  return items;
};

const TITLE_PREDICTED = 'Most Predicted';
const TITLE_TRENDING = 'Top Trending';
const TITLE_MOVERS = 'Biggest Movers';
const TITLE_NEW_ARRIVALS = 'New Arrivals';

const prepareMostPredicted = (markets, sectionTitle) => {
  markets.forEach((el) => {
    el.sectionTitle = sectionTitle;
  });
  return markets;
};

const THROTTLE_TIME = 1750;

const getCachedStoriesByIndexes = (storyMap, indexes) => {
  return indexes.map(index => storyMap[index]).filter(story => typeof story !== 'undefined');
};

const getMissingStoryIndexes = (storyMap, indexes) => {
  return indexes.filter(index => typeof storyMap[index] === 'undefined');
};

export default {
  namespaced: true,
  state: {
    headers: [],
    headersReady: false,
    filters: null,
    orders: ORDERS,
    timespans: TIMESPANS,
    filteredMarkets: Object.create(DEFAULT_FILTERED_MARKETS),
    selectedFilters: [],
    selectedOrder: ORDERS[0],
    selectedTimespan: AVAILABLE_TIMESPANS.TODAY,
    shouldShowTimespanFilter: true,
    twentyFourGains: [],
    twentyFourLoss: [],
    weekGains: [],
    weekLoss: [],
    newArrivalMarkets: [],
    mostPredictedMarkets: [],
    trendingMarkets: [],
    biggestMoverMarkets: [],
    widgets: {},
    latestHeaderId: null,
    storiesNav: [],
    currentStory: null,
    currentStoryIndex: null,
    currentStoryDetail: null,
    storyDetailMap: {},
    storyMap: {},
    storiesList: [],
    storiesUseLegacyPreload: false,
    showNavBrowseOverlay: false,
  },
  getters: {
    areUserBrowseHeadersEmpty(state, getters) {
      return state.headersReady && getters.userBrowseHeaders.length === 0;
    },
    userBrowseHeaders(state, getters, rootState, rootGetters) {
      const baseHeaders = [];

      if (process.env.TOURNAMENT_LINK_ENABLED) {
        baseHeaders.push({
          headerId: -2,
          external: true,
          href: process.env.TOURNAMENT_LINK,
          title: 'Tournament',
          icon: 'star',
        });
      }

      if (rootGetters['user/isLoggedIn']) {
        baseHeaders.push({
          headerId: -1,
          external: true,
          name: 'market-watchlist',
          title: 'Watchlist',
          icon: 'watch',
        });
      }

      return [
        ...baseHeaders,
        ...state.headers,
      ];
    },
  },
  mutations: {
    clearCurrentStoryDetail(state) {
      state.currentStoryDetail = null;
    },
    setCurrentStoryDetail(state, data) {
      state.currentStoryDetail = data;
    },
    clearCurrentStory(state) {
      state.currentStory = null;
    },
    setCurrentStory(state, data) {
      state.currentStory = data;
    },
    setCurrentStoryIndex(state, data) {
      state.currentStoryIndex = data;
    },
    setStoriesNav(state, data) {
      state.storiesNav = data;
    },
    updateStoryDetailMap(state, data) {
      const { index, obj } = data;
      state.storyDetailMap = {
        ...state.storyDetailMap,
        [index]: obj,
      };
    },
    updateStoryMap(state, data) {
      const { index, obj } = data;
      state.storyMap = {
        ...state.storyMap,
        [index]: obj,
      };
    },
    updateStoriesList(state, data) {
      state.storiesList = data;
    },
    setLatestHeaderId(state, data) {
      state.latestHeaderId = data;
    },
    setHeaders(state, payload) {
      state.headers = payload;
      state.headersReady = true;
    },
    setFilters(state, payload) {
      state.filters = payload;
      if (payload) {
        // check if we have selected filters available
        const filterIds = payload.reduce((c, filters) =>
          c.concat(filters.filters.map(f => f.filterId)), []);
        const selectedFilters = intersection(state.selectedFilters, filterIds);
        if (state.selectedFilters.length !== selectedFilters.length) {
          state.selectedFilters = [];
        }
      } else {
        state.selectedFilters = [];
      }
    },
    updateFilteredMarkets(state, payload) {
      const data =
        state.filteredMarkets.data.concat(payload.markets || []);
      const page = payload.page;
      const totalCount = payload.totalMarkets;
      const canLoadMore = totalCount > data.length;

      state.filteredMarkets = {
        ...state.filteredMarkets,
        data,
        page,
        totalCount,
        canLoadMore,
      };
    },
    resetFilteredMarkets(state) {
      state.filteredMarkets = Object.create(DEFAULT_FILTERED_MARKETS);
    },
    updateGainsLossMarketFromNotification(state, data) {
      if (state.twentyFourGains.length > 0) {
        const markets = state.twentyFourGains.slice();
        state.twentyFourGains = markets.map(market => updateMarket(market, data));
      }
      if (state.twentyFourLoss.length > 0) {
        const markets = state.twentyFourLoss.slice();
        state.twentyFourLoss = markets.map(market => updateMarket(market, data));
      }
      if (state.weekGains.length > 0) {
        const markets = state.weekGains.slice();
        state.weekGains = markets.map(market => updateMarket(market, data));
      }
      if (state.weekLoss.length > 0) {
        const markets = state.weekLoss.slice();
        state.weekLoss = markets.map(market => updateMarket(market, data));
      }
    },
    updateGainsLossContractFromNotification(state, data) {
      if (state.twentyFourGains.length > 0) {
        const markets = state.twentyFourGains.slice();
        state.twentyFourGains = markets.map(contract => updateContract(contract, data));
      }
      if (state.twentyFourLoss.length > 0) {
        const markets = state.twentyFourLoss.slice();
        state.twentyFourLoss = markets.map(contract => updateContract(contract, data));
      }
      if (state.weekGains.length > 0) {
        const markets = state.weekGains.slice();
        state.weekGains = markets.map(contract => updateContract(contract, data));
      }
      if (state.weekLoss.length > 0) {
        const markets = state.weekLoss.slice();
        state.weekLoss = markets.map(contract => updateContract(contract, data));
      }
    },
    updatePredictedTrendingMarketFromNotification(state, data) {
      if (state.mostPredictedMarkets.length > 0) {
        const markets = state.mostPredictedMarkets.slice();
        state.mostPredictedMarkets = markets.map(market => updateMarket(market, data));
      }
      if (state.trendingMarkets.length > 0) {
        const markets = state.trendingMarkets.slice();
        state.trendingMarkets = markets.map(market => updateMarket(market, data));
      }
      if (state.biggestMoverMarkets.length > 0) {
        const markets = state.biggestMoverMarkets.slice();
        state.biggestMoverMarkets = markets.map(market => updateMarket(market, data));
      }
      if (state.newArrivalMarkets.length > 0) {
        const markets = state.newArrivalMarkets.slice();
        state.newArrivalMarkets = markets.map(market => updateMarket(market, data));
      }
    },
    updatePredictedTrendingContractsFromNotification(state, data) {
      if (state.mostPredictedMarkets.length > 0) {
        const markets = state.mostPredictedMarkets.slice();
        state.mostPredictedMarkets = markets.map(market => updateMarketContracts(market, data));
      }
      if (state.trendingMarkets.length > 0) {
        const markets = state.trendingMarkets.slice();
        state.trendingMarkets = markets.map(market => updateMarketContracts(market, data));
      }
      if (state.biggestMoverMarkets.length > 0) {
        const markets = state.biggestMoverMarkets.slice();
        state.biggestMoverMarkets = markets.map(market => updateMarketContracts(market, data));
      }
      if (state.newArrivalMarkets.length > 0) {
        const markets = state.newArrivalMarkets.slice();
        state.newArrivalMarkets = markets.map(market => updateMarketContracts(market, data));
      }
    },
    updateMarketWidgetsContractsFromNotification(state, data) {
      if (state.widgets.most) {
        state.widgets = {
          ...state.widgets,
          most: updateContract(state.widgets.most, data),
        };
      }

      if (state.widgets.smart) {
        state.widgets = {
          ...state.widgets,
          smart: updateContract(state.widgets.smart, data),
        };
      }
    },
    updateMarketFromNotification(state, data) {
      if (state.filteredMarkets.data) {
        state.filteredMarkets = {
          ...state.filteredMarkets,
          data: state.filteredMarkets.data.map(market => updateMarket(market, data)),
        };
      }
    },
    updateContractFromNotification(state, data) {
      if (state.filteredMarkets.data) {
        state.filteredMarkets = {
          ...state.filteredMarkets,
          data: state.filteredMarkets.data.map(market => updateMarketContracts(market, data)),
        };
      }
    },
    updateStoryMapFromMarketNotification(state, data) {
      Object.keys(state.storyMap).forEach((key) => {
        const story = state.storyMap[key];
        const desktopContent = story.desktopContent;
        const mobileContent = story.mobileContent;

        if (desktopContent && desktopContent.cmsContent) {
          if (desktopContent.cmsContent.marketTileMarkets) {
            desktopContent.cmsContent.marketTileMarkets =
              desktopContent.cmsContent.marketTileMarkets.map(market => updateMarket(market, data));
          }
          if (desktopContent.cmsContent.contractListMarkets) {
            desktopContent.cmsContent.contractListMarkets =
              desktopContent.cmsContent.contractListMarkets.map(market => updateMarket(market, data));
          }
        }

        if (mobileContent && mobileContent.cmsContent) {
          if (mobileContent.cmsContent.marketTileMarkets) {
            mobileContent.cmsContent.marketTileMarkets =
              mobileContent.cmsContent.marketTileMarkets.map(market => updateMarket(market, data));
          }
          if (mobileContent.cmsContent.contractListMarkets) {
            mobileContent.cmsContent.contractListMarkets =
              mobileContent.cmsContent.contractListMarkets.map(market => updateMarket(market, data));
          }
        }
      });

      if (state.currentStoryDetail && state.currentStoryDetail.cmsContentObject) {
        const cms = state.currentStoryDetail.cmsContentObject;
        if (cms.marketTileMarkets) {
          cms.marketTileMarkets = cms.marketTileMarkets.map(m => updateMarket(m, data));
        }
        if (cms.contractListMarkets) {
          cms.contractListMarkets = cms.contractListMarkets.map(m => updateMarket(m, data));
        }
      }
    },
    updateStoryMapFromContractNotification(state, data) {
      Object.keys(state.storyMap).forEach((key) => {
        const story = state.storyMap[key];
        const desktopContent = story.desktopContent;
        const mobileContent = story.mobileContent;

        if (desktopContent && desktopContent.cmsContent) {
          if (desktopContent.cmsContent.marketTileMarkets) {
            desktopContent.cmsContent.marketTileMarkets =
              desktopContent.cmsContent.marketTileMarkets.map(market => updateMarketContracts(market, data));
          }
          if (desktopContent.cmsContent.contractListMarkets) {
            desktopContent.cmsContent.contractListMarkets =
              desktopContent.cmsContent.contractListMarkets.map(market => updateMarketContracts(market, data));
          }
        }

        if (mobileContent && mobileContent.cmsContent) {
          if (mobileContent.cmsContent.marketTileMarkets) {
            mobileContent.cmsContent.marketTileMarkets =
              mobileContent.cmsContent.marketTileMarkets.map(market => updateMarketContracts(market, data));
          }
          if (mobileContent.cmsContent.contractListMarkets) {
            mobileContent.cmsContent.contractListMarkets =
              mobileContent.cmsContent.contractListMarkets.map(market => updateMarketContracts(market, data));
          }
        }
      });

      if (state.currentStoryDetail && state.currentStoryDetail.cmsContentObject) {
        const cms = state.currentStoryDetail.cmsContentObject;
        if (cms.marketTileMarkets) {
          cms.marketTileMarkets = cms.marketTileMarkets.map(m => updateMarketContracts(m, data));
        }
        if (cms.contractListMarkets) {
          cms.contractListMarkets = cms.contractListMarkets.map(m => updateMarketContracts(m, data));
        }
      }
    },
    addFilter(state, filterId) {
      if (!state.selectedFilters.includes(filterId)) {
        state.selectedFilters.push(filterId);
      }
    },
    clearFilters(state) {
      state.selectedFilters = [];
    },
    clearOrder(state) {
      state.selectedOrder = ORDERS[0];
    },
    setOrderFromQuery(state, order) {
      const found = ORDERS.find(i => i.sort === order);
      if (found) {
        state.selectedOrder = ORDERS.find(i => i.sort === order);
        state.shouldShowTimespanFilter = ORDERS_WITH_TIMESPAN_FILTER
          .indexOf(order) !== -1;
      }
    },
    clearTimespan(state) {
      state.selectedTimespan = AVAILABLE_TIMESPANS.TODAY;
    },
    setTimespanFromQuery(state, timespan) {
      const found = AVAILABLE_TIMESPANS[timespan];
      if (found) {
        state.selectedTimespan = found;
      }
    },
    removeFilter(state, filterId) {
      state.selectedFilters = without(state.selectedFilters, filterId);
    },
    selectOrder,
    selectTimespan,
    setWidgets(state, payload) {
      const mapping = {
        currentlyPredictingWidget: 'currently',
        smartMoneyWidget: 'smart',
        todaysMostPredictedWidget: 'most',
        keyStatsWidget: 'key',
        topCommentsWidget: 'comments',
      };
      const widgets = {};
      Object.keys(payload).forEach((key) => {
        const value = payload[key];
        const name = mapping[key];
        if (name) {
          widgets[name] = value;
        }
      });
      state.widgets = widgets;
    },
    setTwentyFourGains(state, data) {
      state.twentyFourGains = prepareBiggestGains(data, TWENTY_FOUR_GAINS, true);
    },
    setTwentyFourLoss(state, data) {
      state.twentyFourLoss = prepareBiggestGains(data, TWENTY_FOUR_LOSS, false);
    },
    setWeekGains(state, data) {
      state.weekGains = prepareBiggestGains(data, WEEK_GAINS, true);
    },
    setWeekLoss(state, data) {
      state.weekLoss = prepareBiggestGains(data, WEEK_LOSS, false);
    },
    setNewArrivalMarkets(state, data) {
      state.newArrivalMarkets = prepareMostPredicted(data.markets, TITLE_NEW_ARRIVALS);
    },
    setMostPredictedMarkets(state, data) {
      state.mostPredictedMarkets = prepareMostPredicted(data.markets, TITLE_PREDICTED);
    },
    setTrendingMarkets(state, data) {
      state.trendingMarkets = prepareMostPredicted(data.markets, TITLE_TRENDING);
    },
    setBiggestMoverMarkets(state, data) {
      state.biggestMoverMarkets = prepareMostPredicted(data.markets, TITLE_MOVERS);
    },
    toggleNavBrowseCategoriesOverlay(state) {
      state.showNavBrowseOverlay = !state.showNavBrowseOverlay;
    },
  },
  actions: {
    getHeaders({ commit }) {
      const cached = ls.get('browseHeaders');
      const prom = cached ? Promise.resolve({ data: cached }) : api.browse.getHeaders();
      return prom.then((response) => {
        if (!isString(response.data)) {
          const sorted = sortBy(response.data, ['sortOrder']);
          ls.set('browseHeaders', response.data, process.env.LS_TTL_BROWSE_HEADERS);
          commit('setHeaders', sorted);
        }
        return response;
      });
    },
    getFilters({ commit }, headerId) {
      return api.browse.getFilters(headerId)
        .then((response) => {
          const sorted = sortBy(response.data, ['sortOrder']);
          sorted.map((item) => {
            item.filters = sortBy(item.filters, ['sortOrder']);
            return item;
          });
          commit('setFilters', sorted);
          return response;
        });
    },
    getFilteredMarkets: throttle(
      ({ state, commit }, { headerId, reset = false }) => {
        const filterIds = state.selectedFilters.join(',');
        const sort = state.selectedOrder.sort;
        const sortParameter = state.selectedTimespan.parameter;
        const page = (reset) ? 1 : (state.filteredMarkets.page + 1);
        const canLoadMore = (reset) ? true : state.filteredMarkets.canLoadMore;
        if (canLoadMore) {
          return api.browse.getFilteredMarkets(headerId, 20, page, filterIds, sort, sortParameter)
            .then((response) => {
              // resetting takes place here so markets are 'swapped'
              if (reset) commit('resetFilteredMarkets');
              commit('updateFilteredMarkets', Object.assign(response.data, { page }));
              return response;
            });
        }
        return Promise.resolve(1);
      },
      THROTTLE_TIME,
    ),
    resetFilteredMarkets({ commit }) {
      commit('resetFilteredMarkets');
    },
    getNewArrivalMarkets({ commit }, { limit }) {
      return api.browse.getNewArrivalMarkets(limit)
        .then((response) => {
          commit('setNewArrivalMarkets', response.data);
          return response.data;
        });
    },
    getMostPredictedMarkets({ commit }, { limit }) {
      return api.browse.getMostPredictedMarkets(limit)
        .then((response) => {
          commit('setMostPredictedMarkets', response.data);
          return response.data;
        });
    },
    getTrendingMarkets({ commit }, { limit }) {
      return api.browse.getTrendingMarkets(limit)
        .then((response) => {
          commit('setTrendingMarkets', response.data);
          return response.data;
        });
    },
    getBiggestMoverMarkets({ commit }, { limit }) {
      return api.browse.getBiggestMoverMarkets(limit)
        .then((response) => {
          commit('setBiggestMoverMarkets', response.data);
          return response.data;
        });
    },
    addFilter({ commit }, filterId) {
      commit('addFilter', filterId);
    },
    removeFilter({ commit }, filterId) {
      commit('removeFilter', filterId);
    },
    selectOrder({ commit }, sort) {
      commit('selectOrder', sort);
    },
    selectTimespan({ commit }, parameter) {
      commit('selectTimespan', parameter);
    },
    getCarousel() {
      return api.browse.getCarousel()
        .then(response => response.data);
    },
    getTwentyFourGains({ commit }, { isLoss = false, days = 1, contracts = 2 }) {
      return api.browse.getHomePageGainModule(isLoss, days, contracts)
        .then((response) => {
          commit('setTwentyFourGains', response.data);
          return response.data;
        });
    },
    getTwentyFourLoss({ commit }, { isLoss = true, days = 1, contracts = 2 }) {
      return api.browse.getHomePageGainModule(isLoss, days, contracts)
        .then((response) => {
          commit('setTwentyFourLoss', response.data);
          return response.data;
        });
    },
    getWeekGains({ commit }, { isLoss = false, days = 7, contracts = 2 }) {
      return api.browse.getHomePageGainModule(isLoss, days, contracts)
        .then((response) => {
          commit('setWeekGains', response.data);
          return response.data;
        });
    },
    getWeekLoss({ commit }, { isLoss = true, days = 7, contracts = 2 }) {
      return api.browse.getHomePageGainModule(isLoss, days, contracts)
        .then((response) => {
          commit('setWeekLoss', response.data);
          return response.data;
        });
    },
    clearFilters({ commit }) {
      commit('clearFilters');
    },
    clearTimespan({ commit }) {
      commit('clearTimespan');
    },
    setTimespanFromQuery({ commit }, payload) {
      commit('setTimespanFromQuery', payload);
    },
    clearOrder({ commit }) {
      commit('clearOrder');
    },
    setOrderFromQuery({ commit }, payload) {
      commit('setOrderFromQuery', payload);
    },
    updateMarketFromNotification({ commit }, payload) {
      commit('updateMarketFromNotification', payload);
    },
    updateContractFromNotification({ commit }, payload) {
      commit('updateContractFromNotification', payload);
    },
    updateGainsLossMarketFromNotification({ commit }, payload) {
      commit('updateGainsLossMarketFromNotification', payload);
    },
    updateGainsLossContractFromNotification({ commit }, payload) {
      commit('updateGainsLossContractFromNotification', payload);
    },
    updatePredictedTrendingMarketFromNotification({ commit }, payload) {
      commit('updatePredictedTrendingMarketFromNotification', payload);
    },
    updatePredictedTrendingContractsFromNotification({ commit }, payload) {
      commit('updatePredictedTrendingContractsFromNotification', payload);
    },
    updateMarketWidgetsContractsFromNotification({ commit }, payload) {
      commit('updateMarketWidgetsContractsFromNotification', payload);
    },
    updateStoryMapFromMarketNotification({ commit }, payload) {
      commit('updateStoryMapFromMarketNotification', payload);
    },
    updateStoryMapFromContractNotification({ commit }, payload) {
      commit('updateStoryMapFromContractNotification', payload);
    },
    getWidgets({ commit }) {
      return api.browse.getWidgets()
        .then((response) => {
          commit('setWidgets', response.data);
          return response;
        });
    },
    setLatestHeaderId({ commit }, data) {
      commit('setLatestHeaderId', data);
    },
    getStoriesList({ commit, state }, { indexes, cache = false }) {
      if (!Array.isArray(indexes) || indexes.length === 0) {
        return Promise.resolve([]);
      }

      const missingIndexes = cache ? getMissingStoryIndexes(state.storyMap, indexes) : indexes;

      if (cache && missingIndexes.length === 0) {
        const cachedStories = getCachedStoriesByIndexes(state.storyMap, indexes);
        commit('updateStoriesList', cachedStories);
        return Promise.resolve(cachedStories);
      }

      return api.browse.getStoriesList(missingIndexes)
        .then((response) => {
          let stories = [];
          if (Array.isArray(response.data)) {
            stories = response.data;
          } else {
            stories = get(response, 'data.stories', []);
          }

          stories.forEach((obj, position) => {
            commit('updateStoryMap', {
              index: missingIndexes[position],
              obj,
            });
          });

          const orderedStories = getCachedStoriesByIndexes({
            ...state.storyMap,
            ...missingIndexes.reduce((accumulator, index, position) => {
              accumulator[index] = stories[position];
              return accumulator;
            }, {}),
          }, indexes);

          commit('updateStoriesList', orderedStories);
          return orderedStories;
        });
    },
    getStoriesNav({ commit, state }, { cache = true } = {}) {
      if (cache && state.storiesNav.length > 0) {
        return Promise.resolve(state.storiesNav);
      }
      return api.browse.getStoriesNav()
        .then((response) => {
          commit('setStoriesNav', response.data);
          return response.data;
        });
    },
    getStory({ commit, state }, { index, cache = true }) {
      commit('clearCurrentStory');
      if (cache && typeof state.storyMap[index] !== 'undefined') {
        const obj = state.storyMap[index];
        commit('setCurrentStory', obj);
        commit('setCurrentStoryIndex', index);
        return Promise.resolve(obj);
      }
      return api.browse.getStory(index)
        .then((response) => {
          const obj = response.data;
          commit('updateStoryMap', {
            index,
            obj,
          });
          commit('setCurrentStory', obj);
          commit('setCurrentStoryIndex', index);
          return obj;
        });
    },
    getStoryDetail({ commit, state }, { index, cache = true }) {
      commit('clearCurrentStoryDetail');
      if (cache && typeof state.storyDetailMap[index] !== 'undefined') {
        const obj = state.storyDetailMap[index];
        commit('setCurrentStoryDetail', obj);
        return Promise.resolve(obj);
      }
      return api.browse.getStoryDetail(index)
        .then((response) => {
          const obj = response.data;
          commit('updateStoryDetailMap', {
            index,
            obj,
          });
          commit('setCurrentStoryDetail', obj);
          return obj;
        });
    },
    getStaticPageFull({ commit }, key) {
      return api.browse.getStaticPageDetail(key)
        .then(response => response.data);
    },
    getStaticPagePreview({ commit }, id) {
      return api.browse.getStaticPagePreview(id)
        .then(response => response.data);
    },
    getStoryFullPreview({ commit }, id) {
      return api.browse.getStoryFullPreview(id)
        .then(response => response.data);
    },
    clearCurrentStoryDetail({ commit }) {
      commit('clearCurrentStoryDetail');
    },
    toggleNavBrowseCategoriesOverlay({ commit }) {
      commit('toggleNavBrowseCategoriesOverlay');
    }
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/browse.js